'use strict';

const qs = require('qs');
const get = require('lodash.get');
const isEmpty = require('lodash.isempty');
const tokenReplace = require('dibs-token-replace');

const GAC = require('../actions/galleryActionConstants');
const {switchActions} = require('../helpers/GalleryConstants');

const defaultGalleryCopy = require('../../../../json/copy/photoGallery');

/**
 * Checks if the store is ready
 *
 * @returns {boolean}
 */
function isSetup(state) {
    return state.isSetup;
}

/**
 * Checks if photos are loading
 *
 * @returns {boolean}
 */
function isLoadingPhotos({loadingPhotos}) {
    return loadingPhotos;
}

/**
 * Returns the number of photos in the gallery
 *
 * @returns {number}
 */
function getTotalNumberOfPhotos({totalPhotos}) {
    return totalPhotos;
}

/**
 * Returns the count of the fully loaded i.e. available photos
 *
 * @returns {number}
 */
function getPhotosAvailableCount({galleryPhotos}) {
    return galleryPhotos.length;
}

/**
 * Returns the index of the current photo
 *
 * @returns {number}
 */
function getCurrentPhotoIndex({photoIndex}) {
    return photoIndex;
}

/**
 * Returns the last index in the _galleryPhotos array (convenience method)
 *
 * @returns {number}
 */
function getLastPhotoIndex({totalPhotos}) {
    return totalPhotos - 1;
}

/**
 * Returns the gallery copy (or namespaced variable)
 *
 * @param {object} galleryCopy
 * @param {string} [ns]
 * @returns {*}
 */
function getGalleryCopy({galleryCopy}, ns) {
    if (ns) {
        return get(galleryCopy, ns);
    }
    return galleryCopy;
}

/**
 * Returns the current photo (or namespaced variable)
 *
 * @param {object} photo
 * @param {string} [ns]
 * @param {*}      [fallback]
 * @returns {*}
 */
function getCurrentPhoto({photo}, ns, fallback) {
    if (ns) {
        return get(photo, ns, fallback);
    }
    return photo;
}

/**
 * Returns data on the photo's project in an object
 *
 * @param {object} project
 * @param {object} photo
 * @param {string} ns
 * @returns {*}
 */
function getPhotoProject({project, photo}, ns) {
    project = isEmpty(project) ? photo.project : project;
    if (ns) {
        return get(project, ns);
    }
    return project;
}


/**
 * Returns data on the photo's firm in an object
 *
 * @param {object} firm
 * @param {object} photo
 * @param {string} ns
 * @returns {*}
 */
function getPhotoFirm({firm, photo}, ns) {
    firm = isEmpty(firm) ? photo.firm : firm;
    if (ns) {
        return get(firm, ns);
    }
    return firm;
}

/**
 * Returns the index of the photo with the given id
 *
 * @param {array} galleryPhotos
 * @param {string|number} photoId
 */
function getPhotoIndex({galleryPhotos = []}, photoId) {
    photoId = parseInt(photoId);
    return galleryPhotos.findIndex(photo => photo.id === photoId);
}

/**
 * Returns the photo in _galleryPhotos with the given id
 *
 * @param {array} galleryPhotos
 * @param {string|number} photoId
 * @returns {object}
 */
function getPhotoById({galleryPhotos = []}, photoId) {
    photoId = parseInt(photoId);
    return galleryPhotos.find(photo => photo.id === photoId);
}

/**
 * Returns the photo in _galleryPhotos at the given index
 *
 * @param {array} galleryPhotos
 * @param {number} totalPhotos
 * @param {string|number} index
 * @returns {object}
 */
function getPhotoByIndex({galleryPhotos = [], totalPhotos}, index) {
    index = parseInt(index);

    if (index > -1 && index < getTotalNumberOfPhotos({totalPhotos})) {
        return galleryPhotos[index];
    }

    return void 0;
}

/**
 * Returns the photo in _galleryPhotos with the given url
 *
 * @param {array} galleryPhotos
 * @param {string} photoUrl
 * @returns {object}
 */
function getPhotoByUrl({galleryPhotos = []}, photoUrl = '') {
    return galleryPhotos.find(photo => photo.url === photoUrl);
}

/**
 * Returns the next index based on the action
 *
 * @param {number} photoIndex
 * @param {number} totalPhotos
 * @param {string} action
 * @param {number} [index]
 * @returns {number|bool}
 */
function getNewPhotoIndex({photoIndex, totalPhotos}, action, index) {
    index = typeof index === 'number' ? index : photoIndex;

    switch (action) {
        case switchActions.NEXT:
            if (index + 1 > getLastPhotoIndex({totalPhotos})) {
                return 0;
            }
            return index + 1;

        case switchActions.PREVIOUS:
            if (index - 1 < 0) {
                return getLastPhotoIndex({totalPhotos});
            }
            return index - 1;

        // No changes if action is invalid
        default:
            return false;
    }
}

/**
 * Returns gallery url with rt=partial for fetching similar items template
 *
 * @param {Object} state
 * @returns {String}
 */
function getSimilarItemsFullUrl(state) {
    const url = getCurrentPhoto(state, 'url');
    return url + '?' + qs.stringify({
        rt: 'json'
    });
}

/**
 * Returns base url with query params to fetch modal template
 *
 * @returns {string}
 */
function getFetchModalFullUrl(state, {galleryUrl} = {}) {
    galleryUrl = galleryUrl || getCurrentPhoto(state, 'url');
    return galleryUrl + '?' + qs.stringify({
        rt: 'modal'
    });
}

/**
 * Returns base url with query params to load photos
 *
 * @returns {string}
 */
function getLoadPhotosFullUrl(state) {
    let url = state.loadPhotosBaseUrl;
    url += url.indexOf('?') > 0 ? '&' : '?';

    return url + qs.stringify(
        {
            'psz': state.loadPhotosUrlPageSize,
            'pst': getPhotosAvailableCount(state),
            'rt': 'json'
        }
    );
}

/**
 * Get Similar Items tracking for photo
 *
 * @param {Object} state
 */
function getSimilarItemsTracking(state) {
    return get(state, `similarItemTemplates[${getCurrentPhoto(state, 'id')}].tracking`) || {};
}

/**
 * Get Similar Items Urls for photo
 *
 * @param {Object} state
 */
function getSimilarItemsUrls(state) {
    return get(state, `similarItemTemplates[${getCurrentPhoto(state, 'id')}].similarItemUrls`);
}

/**
 * Get Similar Items Template for photo
 *
 * @param {Object} state
 */
function getSimilarItemsTemplate(state) {
    return get(state, `similarItemTemplates[${getCurrentPhoto(state, 'id')}].template`);
}

/**
 * Determines whether the similar items template should be fetched
 *
 * @returns {boolean}
 */
function shouldFetchSimilarItems(state) {
    return !getSimilarItemsTemplate(state);
}

/**
 * Get More Projects Template for photo
 *
 * @param {Object} state
 */
function getMoreProjectsTemplate(state) {
    return get(state, `moreProjectsTemplates[${getPhotoProject(state, 'url')}].moreTemplate`);
}

/**
 * Determines whether the more projects template should be fetched
 *
 * @returns {boolean}
 */
function shouldFetchMoreProjects(state) {
    return !getMoreProjectsTemplate(state);
}

/**
 * Determines whether the photos should asynchronously loaded
 *
 * @returns {boolean}
 */
function shouldLoadPhotos(state) {
    const photosAvailableCount = getPhotosAvailableCount(state);

    return (
        !isLoadingPhotos(state) &&
        photosAvailableCount !== getTotalNumberOfPhotos(state) &&
        (getCurrentPhotoIndex(state) + state.loadPhotosThreshold) >= (photosAvailableCount - 1)
    );
}

/**
 * Helper for building the title string from the photo, firm and project
 *
 * @returns {string}
 */
function getTitleString(state) {
    const copy = getGalleryCopy(state, 'meta.title');
    const tokens = {
        firmName: getPhotoFirm(state, 'name'),
        photoRoom: getCurrentPhoto(state, 'room'),
        photoStyle: getCurrentPhoto(state, 'style'),
        projectLocation: getPhotoProject(state, 'location')
    };
    const replacedSegments = copy.reduce((segments, segment) => {
        const replaced = tokenReplace(segment, tokens);
        if (replaced !== segment) {
            segments.push(replaced);
        }
        return segments;
    }, []);

    return replacedSegments.join(' ');
}

/**
 * Returns the title of the current photo
 *  - if the photo is named, then return the name
 *  - if the photo is not named but has a room type, then return the room type
 *  - otherwise return [Style] [Room] in [Location] by [Firm Name]
 */
function getCurrentPhotoTitle(state) {
    return getCurrentPhoto(state, 'name') || getCurrentPhoto(state, 'room') || getTitleString(state);
}

/**
 * Returns the page title for the <title> tag
 *  - if the photo is named, then return
 *              [Photo Name] - [Style] [Room] in [Location] by [Firm Name]
 *  - otherwise return
 *              [Style] [Room] in [Location] by [Firm Name]
 */
function getCurrentPageTitleTag(state) {
    const name = getCurrentPhoto(state, 'name');
    const title = getTitleString(state);
    return (name ? `${name} - ` : '') + title;
}

/**
 * Helper for building the url string from the photo's urlLabel and root id
 *
 * @param {object} state
 * @param {string} urlLabel
 * @param {number|string} rootId
 * @returns {string}
 */
function buildPhotoUrl(state, {urlLabel, rootId} = {}) {
    if (urlLabel && rootId) {
        // NOTE: leading and trailing slash is important for finding a photo by url
        return `/photo/${urlLabel}/${rootId}/`;
    }
    return void 0;
}

const GalleryStore = {
    storeName: 'GalleryStore',
    initialState: {
        mobileUIState: {
            showDetails: false,
            showSocial: false
        },

        // set default gallery copy (can be overwritten by updateGalleryCopy)
        isSetup: false,
        loadingPhotos: false,
        similarItemTemplates: {},
        moreProjectsTemplates: {},
        galleryCopy: Object.assign({}, defaultGalleryCopy),

        firm: {},
        project: {},
        galleryPhotos: {},

        photo: {},
        photoIndex: 0,
        totalPhotos: 0,

        loadPhotosBaseUrl: '',
        loadPhotosUrlPageSize: 5,
        loadPhotosThreshold: 20
    },

    reducers: {
        [GAC.GALLERY_SETUP_STORE]: function setupStore(state, payload) {
            if (!isSetup(state) && payload) {
                return Object.assign({}, state, {
                    isSetup: true,

                    firm: payload.firm,
                    project: payload.project,
                    galleryPhotos: payload.galleryPhotos,

                    photo: payload.initialPhoto,
                    totalPhotos: payload.totalPhotos,
                    photoIndex: getPhotoIndex(payload, get(payload, 'initialPhoto.id')),

                    loadPhotosBaseUrl: payload.loadPhotosBaseUrl,
                    loadPhotosUrlPageSize: payload.loadPhotosUrlPageSize,
                    loadPhotosThreshold: payload.loadPhotosThreshold
                });
            }

            return state;
        },
        [GAC.GALLERY_RESET_STORE]: function resetStore(state) {
            if (isSetup(state)) {
                return Object.assign({}, state, {
                    isSetup: false
                });
            }
            return state;
        },
        [GAC.GALLERY_UPDATE_COPY]: function updateGalleryCopy(state, payload) {
            if (payload.copy) {
                return Object.assign({}, state, {
                    galleryCopy: Object.assign({}, state.galleryCopy, payload.copy)
                });
            }
            return state;
        },
        [GAC.GALLERY_SET_PHOTO]: function setGalleryPhoto(state, payload) {
            const photo = payload.id ? getPhotoById(state, payload.id) : getPhotoByUrl(state, payload.url);

            if (photo && photo.id !== state.photo.id) {
                return Object.assign({}, state, {
                    photo,
                    photoIndex: getPhotoIndex(state, photo.id)
                });
            }

            return state;
        },
        [GAC.GALLERY_SWITCH_PHOTO]: function switchGalleryPhoto(state, payload) {
            const index = getNewPhotoIndex(state, payload.action);

            if (index < getPhotosAvailableCount(state) && index !== false) {
                // Change the Photo
                return Object.assign({}, state, {
                    photoIndex: index,
                    photo: getPhotoByIndex(state, index)
                });
            }

            return state;
        },
        [GAC.GALLERY_PHOTOS_LOAD_START]: function onPhotosLoading(state) {
            return Object.assign({}, state, {
                loadingPhotos: true
            });
        },
        [GAC.GALLERY_PHOTOS_LOAD_DONE]: function onPhotosLoaded(state, payload = {}) {
            const {photos = []} = payload;

            if (photos && photos.length) {
                return Object.assign({}, state, {
                    loadingPhotos: false,
                    galleryPhotos: state.galleryPhotos.concat(photos)
                });
            }

            return Object.assign({}, state, {loadingPhotos: false});
        },
        [GAC.GALLERY_PHOTOS_LOAD_FAILED]: function onPhotosLoadFailed(state, payload = {}) {
            console.log('Error occured when loading additional photos: ', payload.error);
            return Object.assign({}, state, {
                loadingPhotos: false
            });
        },
        [GAC.GALLERY_ADD_SIMILAR_ITEMS_TEMPLATE]: function addSimilarItemsTemplate(state, {photoId, template, similarItemUrls, tracking}) {
            photoId = photoId || getCurrentPhoto(state, 'id');

            return Object.assign({}, state, {
                similarItemTemplates: Object.assign({}, state.similarItemTemplates, {
                    [photoId]: {template, similarItemUrls, tracking}
                })
            });
        },
        [GAC.GALLERY_ADD_MORE_PROJECTS_TEMPLATE]: function addMoreProjectsTemplate(state, {projectUrl, moreTemplate}) {
            projectUrl = projectUrl || getPhotoProject(state, 'url');
            return Object.assign({}, state, {
                moreProjectsTemplates: Object.assign({}, state.moreProjectsTemplates, {
                    [projectUrl]: {moreTemplate}
                })
            });
        },
        [GAC.GALLERY_MOBILE_SHOW_DETAILS](state, {showDetails} = {}) {
            showDetails = showDetails !== false;

            if (showDetails !== state.mobileUIState.showDetails) {
                return Object.assign({}, state, {
                    mobileUIState: Object.assign({}, state.mobileUIState, {
                        showDetails,
                        showSocial: false
                    })
                });
            }

            return state;
        },
        [GAC.GALLERY_MOBILE_SHOW_SOCIAL](state, {showSocial} = {}) {
            showSocial = showSocial !== false;

            if (showSocial !== state.mobileUIState.showSocial) {
                return Object.assign({}, state, {
                    mobileUIState: Object.assign({}, state.mobileUIState, {
                        showSocial
                    })
                });
            }

            return state;
        }
    },

    getters: {
        isSetup,
        getGalleryCopy,
        isLoadingPhotos,
        shouldLoadPhotos,
        shouldFetchSimilarItems,
        shouldFetchMoreProjects,

        showSocialMobile: state => get(state, 'mobileUIState.showSocial', false),
        showDetailsMobile: state => get(state, 'mobileUIState.showDetails', false),

        getPhotoFirm,
        getPhotoProject,
        getCurrentPhoto,
        getCurrentPhotoIndex,
        getCurrentPhotoAlt: getTitleString,
        getCurrentPhotoId: state => getCurrentPhoto(state, 'id'),
        getCurrentPhotoUrl: state => getCurrentPhoto(state, 'url'),
        getCurrentPhotoSrc: state => getCurrentPhoto(state, 'path'),
        getCurrentPhotoTags: state => getCurrentPhoto(state, 'tags'),
        getCurrentPhotoCaption: state => getCurrentPhoto(state, 'caption'),
        getCurrentPhotoItemTags: state => getCurrentPhoto(state, 'itemTags', []),
        getTitleString,
        getCurrentPhotoTitle,
        getCurrentPageTitleTag,

        getPhotoById,
        getPhotoByIndex,
        getPhotoByUrl,
        getPhotoIndex,
        getLastPhotoIndex,
        getTotalNumberOfPhotos,
        getPhotosAvailableCount,

        getSimilarItemsUrls,
        getSimilarItemsTemplate,
        getSimilarItemsTracking,
        getMoreProjectsTemplate,

        getFetchModalFullUrl,
        getSimilarItemsFullUrl,
        getLoadPhotosFullUrl,

        buildPhotoUrl
    }
};

module.exports = GalleryStore;



// WEBPACK FOOTER //
// ./assets/js/gallery/stores/GalleryStore.es.js